/**
 * Custom Hook: useBlobStorage
 * Manages Azure Blob Storage operations with tenant isolation
 * Automatically uses current tenant from auth context
 */

import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  uploadFileToBlobStorage,
  deleteFileFromBlobStorage,
  listBlobFiles,
  getBlobSasToken,
  generateTenantContainerName,
  generateTenantFolderPath,
  generateUniqueBlobName,
  BlobUploadResponse,
  BlobFileInfo,
  SasTokenResponse,
} from '../api/azureBlobStorageApi';

interface UseBlobStorageState {
  loading: boolean;
  error: string | null;
  files: BlobFileInfo[];
  uploadProgress: number;
}

/**
 * Hook to manage blob storage operations
 * @param containerCategory - e.g., 'employee-documents', 'employee-images'
 * @returns Object with methods and state for blob operations
 */
export const useBlobStorage = (containerCategory: string) => {
  const [state, setState] = useState<UseBlobStorageState>({
    loading: false,
    error: null,
    files: [],
    uploadProgress: 0,
  });

  // Get tenant ID from Redux store (adjust path based on your store structure)
  const tenantId = useSelector((state: any) => {
    // Assuming auth state contains tenantId
    // Adjust this path based on your actual Redux store structure
    return state.auth?.user?.tenantId || state.auth?.tenantId;
  });

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setFiles = useCallback((files: BlobFileInfo[] | ((prev: BlobFileInfo[]) => BlobFileInfo[])) => {
    setState((prev) => ({
      ...prev,
      files: typeof files === 'function' ? files(prev.files) : files,
    }));
  }, []);

  const setUploadProgress = useCallback((progress: number | ((prev: number) => number)) => {
    setState((prev) => ({
      ...prev,
      uploadProgress: typeof progress === 'function' ? progress(prev.uploadProgress) : progress,
    }));
  }, []);

  /**
   * Upload file to blob storage
   */
  const uploadFile = useCallback(
    async (
      file: File,
      entityType: string,
      entityId?: string,
      onProgress?: (progress: number) => void
    ): Promise<BlobUploadResponse | null> => {
      if (!tenantId) {
        setError('Tenant ID not found');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const containerName = generateTenantContainerName(
          containerCategory,
          tenantId
        );
        const folderPath = generateTenantFolderPath(
          tenantId,
          entityType,
          entityId
        );

        // Simulate progress
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
          onProgress?.(Math.min(state.uploadProgress + 10, 90));
        }, 200);

        const response = await uploadFileToBlobStorage(
          containerName,
          file,
          folderPath
        );

        clearInterval(progressInterval);
        setUploadProgress(100);
        onProgress?.(100);

        if (!response.success) {
          throw new Error(response.message || 'Upload failed');
        }

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [tenantId, containerCategory, state.uploadProgress]
  );

  /**
   * Delete file from blob storage
   */
  const deleteFile = useCallback(
    async (blobName: string): Promise<boolean> => {
      if (!tenantId) {
        setError('Tenant ID not found');
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const containerName = generateTenantContainerName(
          containerCategory,
          tenantId
        );
        const result = await deleteFileFromBlobStorage(
          containerName,
          blobName
        );

        if (result.success) {
          setFiles((prev: BlobFileInfo[]) =>
            prev.filter((f: BlobFileInfo) => f.name !== blobName)
          );
          return true;
        } else {
          throw new Error(result.message || 'Delete failed');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Delete failed';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [tenantId, containerCategory]
  );

  /**
   * List all files in a folder
   */
  const listFiles = useCallback(
    async (
      entityType: string,
      entityId?: string
    ): Promise<BlobFileInfo[]> => {
      if (!tenantId) {
        setError('Tenant ID not found');
        return [];
      }

      setLoading(true);
      setError(null);

      try {
        const containerName = generateTenantContainerName(
          containerCategory,
          tenantId
        );
        const folderPath = generateTenantFolderPath(
          tenantId,
          entityType,
          entityId
        );

        const files = await listBlobFiles(containerName, folderPath);
        setFiles(files);
        return files;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'List operation failed';
        setError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [tenantId, containerCategory]
  );

  /**
   * Get SAS token for direct access
   */
  const getSasToken = useCallback(
    async (
      blobName: string,
      expirationMinutes: number = 30
    ): Promise<SasTokenResponse | null> => {
      if (!tenantId) {
        setError('Tenant ID not found');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const containerName = generateTenantContainerName(
          containerCategory,
          tenantId
        );
        return await getBlobSasToken(
          containerName,
          blobName,
          expirationMinutes
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'SAS token failed';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [tenantId, containerCategory]
  );

  /**
   * Generate container name with tenant
   */
  const getContainerName = useCallback(() => {
    if (!tenantId) {
      return null;
    }
    return generateTenantContainerName(containerCategory, tenantId);
  }, [tenantId, containerCategory]);

  /**
   * Generate folder path with tenant
   */
  const getFolderPath = useCallback(
    (entityType: string, entityId?: string) => {
      if (!tenantId) {
        return null;
      }
      return generateTenantFolderPath(tenantId, entityType, entityId);
    },
    [tenantId]
  );

  return {
    // State
    loading: state.loading,
    error: state.error,
    files: state.files,
    uploadProgress: state.uploadProgress,
    tenantId,

    // Methods
    uploadFile,
    deleteFile,
    listFiles,
    getSasToken,
    getContainerName,
    getFolderPath,

    // Utilities
    generateUniqueBlobName,

    // Clear methods
    clearError: () => setError(null),
    clearFiles: () => setFiles([]),
  };
};

export default useBlobStorage;
